import { Check, Edit, MoreHoriz, Warning } from '@mui/icons-material';
import { Chip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButtonLink } from '~/components/button';
import type { NetworkUsers__NetworkMembership as NetworkMembership } from '../../queries/list.generated';
import { Menu } from '../components';

export const useColumns = (): GridColDef<NetworkMembership>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        field: 'name',
        headerName: t('common.labels.name'),
        valueGetter: (_value, row) => row.user.name,
        flex: 1,
      },
      {
        field: 'email',
        headerName: t('common.labels.email'),
        valueGetter: (_value, row) => row.user.email,
        minWidth: 250,
      },
      {
        field: 'roles',
        headerName: t('pages.users.list.columns.roles'),
        minWidth: 250,
        sortable: false,
        valueGetter: (_value, row) => row.user.roles.map((role) => role.name).join(', '),
      },
      {
        field: 'signInCount',
        align: 'center',
        headerAlign: 'center',
        headerName: t('pages.users.list.columns.sign_in_count'),
        sortable: false,
        renderCell: ({ row }) => <Chip color="primary" label={row.user.signInCount} />,
        minWidth: 150,
      },
      {
        field: 'status',
        headerName: t('common.labels.status'),
        sortable: false,
        renderCell: ({ row }) => (
          <>
            <Chip
              color={row.user.status === 'Active' ? 'success' : 'warning'}
              icon={row.user.status === 'Active' ? <Check /> : <Warning />}
              label={t(`pages.users.list.status.${row.user.status.toLowerCase()}`)}
            />
            {row.owner && (
              <Chip
                color="primary"
                label={t('pages.users.list.columns.owner')}
                sx={{ marginLeft: '8px' }}
              />
            )}
          </>
        ),
        minWidth: 160,
      },
      {
        field: 'actions',
        headerName: t('pages.users.list.columns.actions'),
        type: 'actions',
        getActions: ({ row: nm }) => [
          <ActionButtonLink
            key={`edit-${nm.user.id}`}
            title={t('common.actions.edit')}
            Icon={Edit}
            to={`${nm.user.id}`}
          />,
          <Menu
            key={`more-action-${nm.user.id}`}
            networkMembership={nm}
            Icon={MoreHoriz}
            buttonType="action"
          />,
        ],
      },
    ],
    [t],
  );
};
